import { Flex, Tooltip, Typography } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import BatchDelete from "@/components/Elements/BatchDelete";
import Filters, {
  useSchedulingStatesFilter,
  useWithoutTemplateFilter,
} from "@/components/Elements/Filters";
import { useTypesAndSubTypes } from "@/components/Elements/Filters/useTypesAndSubTypes";
import ProgressBar from "@/components/Elements/ProgressBar";
import TableTitleCell from "@/components/Elements/TableTitleCell";
import { TAB_KEYS, TAB_PARAM_KEYS } from "@/constants/TabKeys";
import {
  INTERVENTIONS_FILTERS,
  INTERVENTIONS_INCLUDES,
} from "@/entities/interventions";
import { useInterventionType } from "@/entities/referential/referential-intervention-types";
import { REFERENTIAl_STATES_ENDPOINT } from "@/entities/referential/referential-states";
import {
  InterventionsBatchDissociate,
  InterventionDissociateField,
} from "@/features/Interventions/Dissociate";
import InterventionBreadcrumb from "@/modules/intervention/intervention-breadcrumb";
import {
  InterventionSchedulingIndicator,
  InterventionStateIndicator,
} from "@/modules/intervention/intervention-indicator";
import InterventionsBatchEdit from "@/modules/interventions/interventions-batch-edit";
import { InterventionsListTableRowActions } from "@/modules/interventions/interventions-list-table";
import { useListContext } from "@/modules/list";
import ListTable, { filterListTableColumns } from "@/modules/list-table";
import { ModelLink } from "@/modules/model-link";
import { ModelList } from "@/modules/model-list";
import { useSidePanel } from "@/modules/side-panel";
import { StateTag } from "@/modules/state-tag";
import {
  getTableFilterQueryable,
  TABLE_FILTER_TEXT,
  useTableFilterReferentialStates,
} from "@/modules/table-filter";
import { TranslateCost } from "@/modules/translate";
import { IconIndicator } from "@/ui/icon-indicator";
import { format, fromNow } from "@/utilities/date";

const InterventionsTable = ({
  hiddenColumns = [],
  fixedColumns = true,
  showLocationColumn = false,
  showAssetColumn = false,
  showBatchMode = true,
  showActionsButtons = true,
  showExportButton = true,
  dissociate = undefined,
  ...listTableProps
}) => {
  const { t } = useTranslation();

  const { includes, fetchParams } = useListContext();

  const statesFilter = useTableFilterReferentialStates(
    REFERENTIAl_STATES_ENDPOINT.INTERVENTION
  );
  const schedulingStatesFilter = useSchedulingStatesFilter();
  const withoutTemplateFilter = useWithoutTemplateFilter();
  const { openSidePanel } = useSidePanel();

  const { types = [], subTypes } = useInterventionType();
  const { typesFilter, subTypesFilter } = useTypesAndSubTypes(
    fetchParams[INTERVENTIONS_FILTERS.INTERVENTION_TYPE_ID]
  );

  const onBatchEditClick = (interventions) => {
    openSidePanel({
      title: t("intervention:form.edit.label"),
      component: <InterventionsBatchEdit interventions={interventions} />,
    });
  };

  const onBatchDissociateClick = (models) => {
    if (!dissociate) return;

    openSidePanel({
      title: t("intervention:batchDissociate:title"),
      component: (
        <InterventionsBatchDissociate
          interventionsModels={models}
          dissociateField={dissociate.field}
          dissociateModel={dissociate.model}
          invalidateKeys={dissociate.invalidateKeys}
        />
      ),
    });
  };

  const onBatchDeleteClick = (models) => {
    openSidePanel({
      title: t("intervention:delete.label"),
      component: <BatchDelete type="intervention" models={models} />,
    });
  };

  const batchProps = showBatchMode
    ? {
        onBatchEditClick,
        onBatchDeleteClick,
        onBatchDissociateClick: dissociate ? onBatchDissociateClick : undefined,
      }
    : undefined;

  const columns = [
    {
      title: t("global:name"),
      dataIndex: "name",
      render: (_name, intervention) => (
        <TableTitleCell tooltip={intervention.name} maxWidth="40vw">
          <ModelLink model="intervention" item={intervention} />
        </TableTitleCell>
      ),
      sorter: true,
      fixed: fixedColumns ? "left" : undefined,
      minWidth: 220,
      ...TABLE_FILTER_TEXT,
    },
    {
      title: t("global:type"),
      dataIndex: "intervention_type_id",
      render: (intervention_type_id) => (
        <Typography.Text>
          {types.find(({ id }) => id === intervention_type_id)?.name}
        </Typography.Text>
      ),
      ...typesFilter,
    },
    {
      title: t("global:subtype"),
      render: ({ intervention_type_id, intervention_sub_type_id }) => (
        <Typography.Text>
          {
            subTypes(intervention_type_id).find(
              ({ id }) => id === intervention_sub_type_id
            )?.name
          }
        </Typography.Text>
      ),
      key: "intervention_sub_type_id",
      ...subTypesFilter,
    },
    {
      title: t("global:status"),
      dataIndex: "state",
      render: (state, intervention) => (
        <Flex gap="small">
          <StateTag value={state} type="intervention" />
          <InterventionStateIndicator intervention={intervention} />
        </Flex>
      ),
      sorter: true,
      ...statesFilter,
    },
    {
      title: t("intervention:form.scheduledDate.label"),
      dataIndex: "scheduled_date",
      sorter: true,
      render: (value) => (
        <Tooltip title={format(value, "LLLL")}>{format(value)}</Tooltip>
      ),
    },
    {
      title: t("global:alert"),
      dataIndex: "scheduling_state",
      align: "center",
      render: (value) => (
        <InterventionSchedulingIndicator schedulingState={value} />
      ),
      sorter: true,
      ...schedulingStatesFilter,
    },
    {
      title: <IconIndicator type="withoutTemplate" />,
      dataIndex: "intervention_template_id",
      render: (value) =>
        !value && <IconIndicator type="withoutTemplate" title={value} />,
      ...withoutTemplateFilter,
    },
    {
      title: t("global:cost"),
      dataIndex: "cost",
      align: "right",
      sorter: true,
      render: (cost) => cost && <TranslateCost value={cost} />,
    },
    {
      title: t("intervention:form.progress.label"),
      dataIndex: "progress",
      sorter: true,
      ...Filters.progress,
      render: (progress) => <ProgressBar value={progress} />,
    },
    {
      title: t("intervention:recommendedBy.label"),
      dataIndex: "recommended_by",
      render: (recommended_by) =>
        recommended_by && (
          <ModelLink
            model="intervention"
            item={recommended_by}
            aria-label={t("intervention:seeParentIntervention.label")}
          >
            {recommended_by.name}
          </ModelLink>
        ),
    },
    {
      title: t("intervention:recommendedInterventions.label"),
      dataIndex: "recommended_interventions_count",
      render: (recommended_interventions_count, intervention) =>
        recommended_interventions_count > 0 && (
          <ModelLink
            model="intervention"
            item={intervention}
            search={`?${TAB_PARAM_KEYS.CONTEXT_TAB_KEY}=${TAB_KEYS.INTERVENTIONS_RECOMMENDATIONS}`}
            aria-label={t("intervention:seeChildrenInterventions.label")}
          >
            {recommended_interventions_count}
          </ModelLink>
        ),
    },
    {
      title: t("global:location"),
      dataIndex: ["asset", "ancestors"],
      render: (_ancestors, intervention) => (
        <InterventionBreadcrumb
          intervention={intervention}
          include={["location"]}
          hideIcon
          ellipsis
          compact
        />
      ),
      key: "asset.location_id",
      filtersIfCan: "view.location",
      ...getTableFilterQueryable({ model: "locations", withContext: true }),
      hidden: !showLocationColumn,
    },
    {
      title: t("global:asset"),
      dataIndex: "asset",
      render: (_asset, intervention) => (
        <InterventionBreadcrumb
          intervention={intervention}
          include={["asset"]}
          hideIcon
          ellipsis
          compact
        />
      ),
      sorter: true,
      key: "asset.name",
      ...TABLE_FILTER_TEXT,
      hidden: !showAssetColumn,
    },
    {
      title: t("global:project"),
      dataIndex: "project_id",
      render: (_project_id, { project }) =>
        project ? <ModelLink model="project" item={project} /> : "",
      filtersIfCan: "view.project",
      ...getTableFilterQueryable({ model: "projects", withContext: true }),
      hidden: !includes.includes(INTERVENTIONS_INCLUDES.PROJECT),
    },
    {
      title: t("global:userPlural"),
      dataIndex: ["users", "id"],
      render: (_, { users }) => (
        <ModelList model="user" items={users} withLink />
      ),
      filtersIfCan: "view.user",
      ...getTableFilterQueryable({ model: "users" }),
    },
    {
      title: t("global:lastUpdate"),
      dataIndex: "updated_at",
      sorter: true,
      render: (value) => (
        <Tooltip title={format(value, "LLLL")}>{fromNow(value)}</Tooltip>
      ),
    },
    {
      title: "",
      fixed: fixedColumns ? "right" : undefined,
      render: (intervention) => (
        <InterventionsListTableRowActions
          intervention={intervention}
          dissociateInfos={dissociate}
        />
      ),
      hidden: !showActionsButtons,
    },
  ];

  const filtredColumns = filterListTableColumns({
    columns,
    hiddenColumns,
  });

  return (
    <ListTable
      columns={filtredColumns}
      showExportButton={showExportButton}
      {...batchProps}
      {...listTableProps}
    />
  );
};

InterventionsTable.propTypes = {
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  fixedColumns: PropTypes.bool,
  showLocationColumn: PropTypes.bool,
  showAssetColumn: PropTypes.bool,
  showBatchMode: PropTypes.bool,
  showActionsButtons: PropTypes.bool,
  showExportButton: PropTypes.bool,
  dissociate: PropTypes.shape({
    model: PropTypes.object,
    field: InterventionDissociateField,
    invalidateKeys: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  }),
};

export default InterventionsTable;
